import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AssetNodeType } from '@festo-ax/smartenance-shared-ui';
import { AssetStateEnum, SAP_MARKER } from 'app/models/enums/asset';
import { LicenseFeatureFlag } from 'app/models/license';
import { SelectedSapAsset } from 'app/models/reducers/sap-assets';
import { NgClass } from '@angular/common';
import { FngTooltipDirective } from '@festo-ui/angular';
import { TranslateModule } from '@ngx-translate/core';
import { FeatureOnDirective } from 'app/directives/feature-on.directive';

@Component({
  selector: 'sma-web-asset-layout',
  templateUrl: 'asset-layout.component.html',
  styleUrls: ['asset-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    TranslateModule,
    FngTooltipDirective,
    FeatureOnDirective,
  ],
})
export class AssetLayoutComponent {
  private router: Router = inject(Router);

  @Input() isAssetOpen: boolean;
  @Input() isNestedViewState: boolean;

  @Input() set showAsset(value: boolean) {
    this.showOpenButton = value;
    if (!value) {
      this.isAssetOpen = false;
    }
  }

  @Input() assetNodeId: string | null;
  @Input() assetMachineId: string | null;
  @Input() assetSapMachineId: string | null;
  @Input() showExportMenu: boolean;
  @Input() sapSelected: SelectedSapAsset;
  @Input() isRegularMachineZone: boolean;
  showOpenButton: boolean;

  readonly AssetStateEnum: typeof AssetStateEnum = AssetStateEnum;
  readonly LicenseFeatureFlag: typeof LicenseFeatureFlag = LicenseFeatureFlag;

  switchToCloseIfOpen(isAssetOpen: boolean): void {
    // when we navigate between sap asset and regular asset we should always open first assets
    // so first call from sap to regular should be skipped
    const isFirstCall: boolean = this.router.url.includes(SAP_MARKER);

    if (!isAssetOpen || isFirstCall) {
      return;
    }

    const primary: string[] = this.assetMachineId
      ? ['machines', this.assetMachineId]
      : ['machines'];

    const asset: string[] = this.assetMachineId
      ? ['tree', AssetStateEnum.CLOSE, AssetStateEnum.REGULAR, this.assetNodeId, this.assetMachineId]
      : ['tree', AssetStateEnum.CLOSE, AssetStateEnum.REGULAR, this.assetNodeId];

    // if asset open we need to close it
    this.router.navigate([{ outlets: { primary, asset } }]);
  }

  switchToCloseIfOpenSap(isAssetOpen: boolean): void {
    // when we navigate between sap asset and regular asset we should always open first assets
    // so first call from regular to sap should be skipped
    const isFirstCall: boolean = !this.router.url.includes(SAP_MARKER);

    if (!isAssetOpen || isFirstCall) {
      return;
    }
    // if asset open we need to close it
    const primary: string[] = this.sapSelected?.nodeType === AssetNodeType.MACHINE
      ? ['sap', 'machines', this.sapSelected.nodeId]
      : ['sap', 'machines']
    ;

    const nodeId: string | null = this.sapSelected?.nodeId || 'root';
    const nodeType: AssetNodeType | null = this.sapSelected?.nodeType || AssetNodeType.GROUP;
    const asset: string[] = ['tree', AssetStateEnum.CLOSE, AssetStateEnum.SAP, nodeType, nodeId];

    const commands: unknown[] = [{ outlets: { primary, asset } }];

    this.router.navigate(commands);
  }
}
